import { openProfileDocument } from "../../../backend/transport";
import React, { useEffect, useState } from 'react';
import { SearchBar, TitleBar, BlpTiles, DropDown } from '../../common';
import {
  BUTTON,
  HEADING,
  PKJ_STATUS,
  POPUP_MSG,
  POPUP_TYPE,
  ROUTES,
  TITLES,
} from '../../../constants';
import { Button } from '../../shared';
import { useNavigate, useSearchParams } from "../../../backend/navigation";
import { StatusBox } from '../manufacturing/clothManufacturing/StatusBox';
import { popup } from '../../../utils';
import { vendorPackage, printLabelPackage } from '../../../service/package';
import { useDebounce } from '../../../hooks';
import { packageDataCompiler } from '../../../utils/dataCompiler/packageDataCompiler';
import PrintLabelModalBulk from './PrintLabelModalBulk';

export const LegacyPackageDetail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [packageData, setPackageData] = useState([]);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sort1, setSort1] = useState(searchParams.get('boxType') || 'Box Type');
  const [printMode, setPrintMode] = useState(searchParams.get('printMode') === 'true' || false);
  const [selectedBlp, setSelectedBlp] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [jumpPage, setJumpPage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState(() => ({ term: searchTerm, boxType: sort1, page: pageNumber }));

  const [selectionMode, setSelectionMode] = useState("page"); // "page" | "all"
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [globalSelectAll, setGlobalSelectAll] = useState(false);

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filters if they're not the default values
    if (params.boxType && params.boxType !== 'Box Type') newParams.boxType = params.boxType;
    if (params.page) newParams.page = params.page.toString();
    if (params.printMode !== undefined) newParams.printMode = params.printMode.toString();

    setSearchParams(newParams, { replace: true });
  };

  const onTileClick = (to, data) => {
    navigate(to, { state: data });
  };

  const getPackageList = useDebounce(({ term, boxType, page }) => {
    setIsLoading(true);
    setGlobalSelectAll(false);
    setSelectionMode("page");

    const params = {
      page_number: page,
      search: term
        ? {
          name: term,
          'quant_ids.lot_id.name': term,
          'quant_ids.product_id.default_code': term,
        }
        : {},
      sort:
        boxType === 'Box Type'
          ? {}
          : { selection: { is_mixed: boxType === 'Single Box' ? false : true } },
    };

    // Update URL params
    updateUrlParams({
      search: term,
      boxType: boxType,
      page: params.page_number,
      printMode: printMode,
    });


    vendorPackage({ data: params })
      .then((response) => {
        setIsLoading(false);
        const records = response?.data?.records || [];
        if (records.length > 0) {
          const compiledData = packageDataCompiler(records);
          setPackageData(compiledData);
          setPaginationData(response.data.params);
          if (!sortingArray1.includes(sort1)) {
            setSortingArray1(['Single Box', 'Mix Box']);
          }
        } else {
          setPackageData([]);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, 400);

  useEffect(() => {
    getPackageList(query);
  }, [getPackageList, query]);

  const submitQuery = ({ term = searchTerm.trim(), boxType = sort1, page = 1 } = {}) => {
    setPageNumber(page);
    setQuery({ term, boxType, page });
  };
  const handleSearch = (value) => {
    setSearch(value);
    if (value) submitQuery();
  };
  const handlePageChange = (value) => {
    const page = typeof value === 'function' ? value(pageNumber) : value;
    if (page && page !== pageNumber) submitQuery({ page });
  };
  const handleSortChange = (boxType) => {
    setSort1(boxType);
    submitQuery({ boxType });
  };
  const updatePrintMode = (value) => {
    setPrintMode(value);
    updateUrlParams({ search: searchTerm, boxType: sort1, page: pageNumber, printMode: value });
    if (!value) {
      setSelectedBlp([]);
      setSelectionMode("page");
      setExcludedIds(new Set());
      setGlobalSelectAll(false);
    }
  };
  const clearSearch = () => {
    setSearch(false);
    submitQuery({ term: '' });
  };
  const resetFilter = () => {
    setSort1('Box Type');
    submitQuery({ boxType: 'Box Type' });
  };

  const addPackageToList = (id) => {
    if (selectionMode === "all") {
      const next = new Set(excludedIds);
      if (excludedIds.has(id)) {
        next.delete(id); // re-check
      } else {
        next.add(id); // uncheck
      }
      setExcludedIds(next);
    } else {
      if (selectedBlp.includes(id)) {
        // remove id
        setSelectedBlp((prev) => prev.filter((pkgId) => pkgId !== id));
      } else {
        // add id
        setSelectedBlp((prev) => [id, ...prev]);
      }
    }
  };

  const checked = (id) => {
    if (selectionMode === "all") {
      return !excludedIds.has(id);
    }
    return selectedBlp.includes(id);
  };

  const handleSelectAllVisible = () => {
    const visiblePackageIds = packageData.map(pkg => pkg.id);
    visiblePackageIds.forEach(id => {
      if (!checked(id)) {
        addPackageToList(id);
      }
    });
    setSelectionMode("page");
    setExcludedIds(new Set());
  };

  const handleSelectAllMatching = () => {
    setSelectionMode("all");
    setExcludedIds(new Set());
    setGlobalSelectAll(true);
    setSelectedBlp([]);
  };

  const bulkPrint = () => {
    if (printMode) {
      const totalSelected = selectionMode === "all"
        ? paginationData.total_records - excludedIds.size
        : selectedBlp.length;

      if (totalSelected > 100) {
        popup(POPUP_TYPE.ERR, POPUP_MSG.PRINT_LIMIT);
      } else if (totalSelected > 0) {
        setIsModalOpen(true);
      } else {
        popup(POPUP_TYPE.ERR, 'No Package Selected');
      }
    } else {
      updatePrintMode(true);
      popup(POPUP_TYPE.SUCCESS, POPUP_MSG.SELECT_PACKAGE);
    }
  };

  const cancelPrint = () => {
    updatePrintMode(false);
    setSelectedBlp([]);
    setSelectionMode("page");
    setExcludedIds(new Set());
    setGlobalSelectAll(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmitModal = (payload) => {
    let finalPayload;

    if (selectionMode === "all") {
      // Global selection payload - similar to BillsContent
      const sortSelection = sort1 === 'Box Type'
        ? {}
        : { is_mixed: sort1 === 'Single Box' ? false : true };

      finalPayload = {
        ...payload,
        select_all: true,
        search: searchTerm
          ? {
            name: searchTerm,
            'quant_ids.lot_id.name': searchTerm,
            'quant_ids.product_id.default_code': searchTerm,
          }
          : {},
        sort: {
          selection: Object.keys(sortSelection).length > 0 ? sortSelection : {},
        },
        excluded_ids: Array.from(excludedIds),
      };
    } else {
      // Page selection payload - existing logic
      finalPayload = {
        ...payload,
        select_all: false,
        package_ids: selectedBlp,
      };
    }

    // Make an API call or perform other actions with the payload
    printLabelPackage({
      data: finalPayload,
    })
      .then((response) => {
        if (!response) return;
        if (response.data.length > 0) {
          const url = response.data[0].split('?')[0];
          openProfileDocument(url, '_blank', 'noreferrer');
          updatePrintMode(false);
          setSelectedBlp([]);
          setSelectionMode("page");
          setExcludedIds(new Set());
          setGlobalSelectAll(false);
        }
      });
  };

  const handleJumpToPage = () => {
    const totalPages = Math.ceil(paginationData.total_records / 20);
    const targetPage = parseInt(jumpPage, 10);

    if (targetPage >= 1 && targetPage <= totalPages) {
      handlePageChange(targetPage);
    } else {
      popup(POPUP_TYPE.ERR, `Please enter a valid page number between 1 and ${totalPages}.`);
    }
  };

  return (
    <>
      <TitleBar title={[{ text: TITLES.PACKAGE, route: ROUTES.PACKAGE }]} />
      <div className="bg-white p-3 package-top-info border-def-up border-def-down">
        <StatusBox status={PKJ_STATUS} />
        <div className="d-flex align-items-center justify-content-end mb-3 top-header-set">
          <div className="d-flex align-items-center">
            <h4 className="me-3 mb-0">{HEADING.BOX_COUNT}</h4>
            <div className="number-count d-flex align-items-center justify-content-center">
              {Array.isArray(packageData) && packageData.length ? packageData?.length : 0}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-start gap-3 w-100">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={handleSearch}
            onDefaultSearch={clearSearch}
          />
          <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
            <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={handleSortChange} />
            {sortingArray1.includes(sort1) && (
              <Button
                clickHandler={resetFilter}
                className="blue-btn d-flex align-items-center justify-content-center px-3">
                {BUTTON.CLEAR_FILTERS}
              </Button>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-between gap-2">
          <div className="d-flex justify-content-start align-items-center gap-2">
            {printMode && (
              <>
                {!globalSelectAll && (
                  <Button
                    clickHandler={handleSelectAllVisible}
                    className="blue-btn d-flex align-items-center justify-content-center px-4 ">
                    Select Page
                  </Button>
                )}

                {/* Selection count and "Select All" link - similar to BillsContent */}
                {(selectedBlp.length > 0 && !globalSelectAll) && (
                  <>
                    <span className="text-muted">
                      {selectedBlp.length} {selectedBlp.length === 1 ? 'record' : 'records'} selected
                    </span>
                    {!(selectedBlp.length === paginationData?.total_records) && (
                      <span
                        onClick={handleSelectAllMatching}
                        className="text-primary cursor-pointer fw-semibold"
                        role="button"
                      >
                        Select All ({paginationData?.total_records})
                      </span>
                    )}
                  </>
                )}

                {/* Global selection display - similar to BillsContent */}
                {selectionMode === 'all' && (
                  <>
                    <span className="text-muted">
                      {paginationData?.total_records - excludedIds.size}{' '}
                      {(paginationData?.total_records - excludedIds.size) === 1 ? 'record' : 'records'} selected
                    </span>
                    {excludedIds.size > 0 && (
                      <span
                        onClick={handleSelectAllMatching}
                        className="text-primary cursor-pointer fw-semibold"
                        role="button"
                      >
                        Select All ({paginationData?.total_records})
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {Array.isArray(packageData) && packageData?.length > 0 && (
            <div className="d-flex justify-content-end gap-2">
              <Button
                clickHandler={bulkPrint}
                className="white-btn d-flex align-items-center justify-content-center px-4 ">
                {BUTTON.PRINT_LABEL}
              </Button>
              {printMode && (
                <Button
                  clickHandler={cancelPrint}
                  className="white-btn d-flex align-items-center justify-content-center px-4 ">
                  {BUTTON.CANCEL}
                </Button>
              )}
              {!printMode && (
                <Button
                  clickHandler={() => navigate(ROUTES.ADD_MIX_BOX, { state: packageData })}
                  className="white-btn d-flex align-items-center justify-content-center ms-2 px-3 ">
                  {BUTTON.ADD_BOX}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {Array.isArray(packageData) && packageData.length > 0 && (
        <div className="pagination-container d-flex justify-content-between align-items-center mb-2">
          <h5 className="pagination-info">
            <span className="highlight">Records:</span>
            <span className="record-range">
              {paginationData.start} - {paginationData.end}
            </span>
            <span className="highlight">of</span>
            <span className="total-records">{paginationData.total_records}</span>
          </h5>

          <div className="pagination-controls d-flex align-items-center justify-content-end">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${paginationData.page === 1 ? 'disabled' : ''
                }`}
              clickHandler={() => handlePageChange((prev) => Math.max(prev - 1, 1))}
              disabled={paginationData.page === 1}>
              Previous
            </Button>
            <div className="page-jump-container d-flex align-items-center my-2">
              <input
                type="number"
                className="page-jump-input form-control me-2"
                placeholder={`On page ${paginationData.page} of ${Math.ceil(paginationData.total_records / 20)}`}
                value={jumpPage}
                disabled={Math.ceil(paginationData.total_records / 20) === 1}
                readOnly={Math.ceil(paginationData.total_records / 20) === 1}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === '' ||
                    (/^[0-9]+$/.test(value) &&
                      value > 0 &&
                      value <= Math.ceil(paginationData.total_records / 20))
                  ) {
                    setJumpPage(value);
                  } else {
                    setJumpPage('');
                  }
                }}
              />
              <Button
                className={`blue-btn ${Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                  ? 'disabled'
                  : ''
                  } px-3`}
                clickHandler={() => {
                  handleJumpToPage();
                  setJumpPage('');
                }}
                disabled={
                  Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  Number(paginationData.page) === Number(jumpPage)
                }>
                Go
              </Button>
            </div>

            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 mx-1 ${paginationData.page === Math.ceil(paginationData.total_records / 20)
                ? 'disabled'
                : ''
                }`}
              clickHandler={() =>
                handlePageChange((prev) =>
                  Math.min(prev + 1, Math.ceil(paginationData.total_records / 20)),
                )
              }
              disabled={paginationData.page === Math.ceil(paginationData.total_records / 20)}>
              Next
            </Button>
          </div>
        </div>
      )}
      <div className="bg-white p-3 pt-0 package-titles-info pkj-tile-view">
        <div className="row custom-row">
          {isLoading ? (
            <div className="text-center my-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          ) : Array.isArray(packageData) && packageData.length > 0 ? (
            packageData.map((tile, index) => (
              <BlpTiles
                key={index}
                tile={tile}
                clickHandler={printMode ? bulkPrint : onTileClick}
                to={ROUTES.PACKAGE_BLP_OVERVIEW}
                printMode={printMode}
                selectedBlp={selectedBlp}
                setSelectedBlp={setSelectedBlp}
                addPackageToList={addPackageToList}
                checked={checked}
                selectionMode={selectionMode}
              />
            ))
          ) : (
            <div>
              <h5 className="px-1 text-center py-5">No Records Found</h5>
            </div>
          )}

        </div>
      </div>
      {isModalOpen && (
        <PrintLabelModalBulk
          onClose={handleCloseModal}
          onSubmit={handleSubmitModal}
          packageData={selectedBlp}
        />
      )}
    </>
  );
};